import { useMutation } from "@apollo/client/react";
import { CheckIcon, XIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { Mutations } from "@/constants";
import { EnumColors } from "@/constants/values";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { usePendingChangeStatus } from "./ToolCallCard.hooks";

/**
 * Where a tool call's change stands, in the card's header beside its chevron:
 * Confirmed / Rejected once decided, Reject / Confirm while it is pending -
 * an icon button each, named under the pointer.
 * It reads the record the card has already loaded, so it shows whether or not
 * the card is expanded.
 */
export function ToolCallCardPendingStatus({
  node,
  toolName,
  pendingChangeIdentifier,
  pendingChangeType,
  scopedChangeIdentifiers,
  isLoading = false,
  hideDecision = false,
  isUndecided = false,
}: {
  node: unknown;
  toolName?: string | null;
  pendingChangeIdentifier?: string | null;
  pendingChangeType?: string | null;
  /**
   * Every change these buttons decide. A record the agent wrote and then
   * edited has more than one, and deciding it means deciding all of them -
   * rejecting the edit alone would leave the record it was made to. Defaults
   * to the one change this reads.
   */
  scopedChangeIdentifiers?: string[];
  /**
   * The call is still running, or its record has not loaded. Reject and
   * Confirm hold their place as skeletons rather than appearing from nowhere
   * once everything lands.
   */
  isLoading?: boolean;
  /**
   * Confirmed / Rejected left to whatever holds these, which says so itself -
   * a row of the changes tray: here it only showed again beside it.
   */
  hideDecision?: boolean;
  /**
   * Known to await review - a row of the changes tray, which lists only those:
   * Reject and Confirm, deciding ``scopedChangeIdentifiers``, drawn at once
   * rather than after reading the change.
   */
  isUndecided?: boolean;
}) {
  const {
    hasChanges,
    isHistorical,
    isConfirmed,
    changeIdentifier,
    pendingSession,
    showsStatus,
    isLoadingChange,
  } = usePendingChangeStatus({
    node,
    toolName,
    pendingChangeIdentifier,
    pendingChangeType,
  });
  // Each card reads its change once and keeps it, so once a change is decided
  // every card re-reads its own: otherwise a rejected change still read as
  // pending, and offered Reject / Confirm again as soon as a later change
  // opened a new review. Re-read from the server, so a rejection that took
  // other changes with it shows on their cards too.
  const [confirmPendingNodes, { loading: isConfirming }] = useMutation(
    Mutations.CONFIRM_PENDING_NODES,
    { refetchQueries: ["PendingChangeQuery"] },
  );
  const [rejectPendingNodes, { loading: isRejecting }] = useMutation(
    Mutations.REJECT_PENDING_NODES,
    { refetchQueries: ["PendingChangeQuery"] },
  );

  // Still loading - the call running, or its record or change not in - the
  // buttons hold their place, each drawn as its skeleton.
  const isWaiting = !isUndecided && (isLoading || isLoadingChange);

  if (!isWaiting && !isUndecided) {
    if (!showsStatus || !hasChanges) return null;
    if (isHistorical) {
      if (hideDecision) return null;
      return (
        <SimpleEnum
          label={isConfirmed ? "Confirmed" : "Rejected"}
          enumColor={isConfirmed ? EnumColors.GREEN : EnumColors.RED}
          size="sm"
        />
      );
    }
  }

  const sessionIdentifier = pendingSession?.pendingSessionId;
  if (!isWaiting && (!(changeIdentifier || isUndecided) || !sessionIdentifier))
    return null;
  const input = {
    sessionIdentifier: sessionIdentifier ?? "",
    scopedChangeIdentifiers: scopedChangeIdentifiers ?? [
      changeIdentifier ?? "",
    ],
  };

  const reject = () =>
    rejectPendingNodes({ variables: { input } }).then(() =>
      pendingSession?.onRefetch(),
    );
  const confirm = () =>
    confirmPendingNodes({ variables: { input } }).then(() =>
      pendingSession?.onRefetch(),
    );

  // The header toggles the card open; these act on the change instead.
  return (
    <div className="flex gap-1" onClick={(event) => event.stopPropagation()}>
      {(
        [
          ["Reject", XIcon, isRejecting, reject],
          ["Confirm", CheckIcon, isConfirming, confirm],
        ] as const
      ).map(([label, ReactIcon, loading, decide]) => (
        <Skeleton
          key={label}
          loading={isWaiting}
          variant="rectangular"
          className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit] rounded-sm"
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={label}
                disabled={loading || isWaiting}
                onClick={decide}
              >
                {loading ? (
                  <Spinner />
                ) : (
                  <Icon ReactIcon={ReactIcon} size="sm" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{label}</TooltipContent>
          </Tooltip>
        </Skeleton>
      ))}
    </div>
  );
}
